import React, { useEffect, useMemo, useState } from "react";

import { Button, Icon } from "../../components/tailwind";
import { Dropdown, Option } from "../../components/tailwind/dropdown/Dropdown";
import {
  CustomerField,
  useGetCustomerFieldsQuery,
} from "../../api/customerFieldApi";
import { FieldType } from "../../api/enrichApi";
import { CustomerFieldEditModal } from "../../customers/customer-fields/CustomerFieldEditModal";

const LABEL_CLASSES =
  "tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1";

/**
 * Mirror of `StructuredOutputBuilder.validate` (gpt/structured_output/builder.py).
 * Lets us warn before the serializer rejects the field with a 400.
 */
export const validateStructuredOutputField = (
  field: CustomerField
): { ok: boolean; reason: string } => {
  if (!field.type) {
    return { ok: false, reason: "Missing type" };
  }
  if (
    field.type === FieldType.SINGLE_SELECT_LIST ||
    field.type === FieldType.MULTI_SELECT_LIST
  ) {
    if (!field.list_values || field.list_values.length === 0) {
      return { ok: false, reason: "List values missing" };
    }
    if (field.list_values.some((v) => !v.value?.trim() || !v.label?.trim())) {
      return { ok: false, reason: "List values are incomplete" };
    }
  }
  if (field.type === FieldType.LANGUAGE_STRING) {
    return {
      ok: false,
      reason: "LANGUAGE_STRING field type is not supported for metadata output",
    };
  }
  return { ok: true, reason: "" };
};

type Props = {
  value: string[];
  onChange: (fieldIds: string[]) => void;
  // Reports whether the current selection would pass backend validation, so the
  // parent form can disable Save.
  onValidityChange: (isValid: boolean) => void;
};

export const PromptMetadataFields: React.FC<Props> = ({
  value,
  onChange,
  onValidityChange,
}) => {
  const { data, isLoading } = useGetCustomerFieldsQuery({});
  const fields = data?.fields ?? [];

  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);

  const fieldByFieldId = useMemo(() => {
    const map = new Map<string, CustomerField>();
    for (const field of fields) {
      map.set(field.field_id, field);
    }
    return map;
  }, [fields]);

  const options = useMemo<Option<string>[]>(
    () =>
      fields.map((field) => ({
        key: String(field.id),
        value: field.field_id,
        text: field.name || field.field_id,
        caption: field.type || "No type set",
      })),
    [fields]
  );

  // Selected ids that no longer resolve to a CustomerField (deleted / renamed).
  const missingFieldIds = useMemo(
    () => value.filter((id) => !fieldByFieldId.has(id)),
    [value, fieldByFieldId]
  );

  // Selected, resolvable fields that fail validation.
  const invalidFields = useMemo(() => {
    const result: { field: CustomerField; reason: string }[] = [];
    for (const id of value) {
      const field = fieldByFieldId.get(id);
      if (!field) {
        continue;
      }
      const { ok, reason } = validateStructuredOutputField(field);
      if (!ok) {
        result.push({ field, reason });
      }
    }
    return result;
  }, [value, fieldByFieldId]);

  const isValid =
    !isLoading && missingFieldIds.length === 0 && invalidFields.length === 0;

  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  const removeFieldId = (fieldId: string): void => {
    onChange(value.filter((id) => id !== fieldId));
  };

  return (
    <div className="tw-space-y-3">
      <div>
        <label className={LABEL_CLASSES}>Metadata Fields</label>
        <p className="tw-text-sm tw-text-gray-500 tw-mb-2 tw-mt-0">
          The AI result is written into these product fields instead of a text
          section. Later prompts can read these values from their Input Schema.
        </p>
        <Dropdown
          control="multi-search"
          testIdBase="prompt-form-structured-output-fields"
          options={options}
          value={value}
          onChange={(next) => onChange(next as string[])}
          placeholder={
            isLoading ? "Loading fields..." : "Select fields to write to..."
          }
          clearable
          badgeProps={{ clearable: true, active: true }}
        />
      </div>

      {value.length === 0 && !isLoading && (
        <p className="tw-text-sm tw-text-amber-700 tw-m-0">
          Select at least one field for the prompt to write to.
        </p>
      )}

      {missingFieldIds.length > 0 && (
        <div className="tw-rounded-md tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-space-y-2">
          {missingFieldIds.map((fieldId) => (
            <div
              key={fieldId}
              className="tw-flex tw-items-center tw-justify-between tw-gap-3"
            >
              <span className="tw-text-sm tw-text-red-700">
                <Icon name="warning" className="tw-mr-1" />
                <span className="tw-font-mono">{fieldId}</span> does not exist
                for this customer.
              </span>
              <Button
                type="button"
                variant="secondary-alt"
                size="sm"
                compact
                onClick={() => removeFieldId(fieldId)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      {invalidFields.length > 0 && (
        <div className="tw-rounded-md tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3 tw-space-y-2">
          <p className="tw-text-sm tw-font-medium tw-text-amber-900 tw-m-0">
            Some selected fields are not configured for metadata output:
          </p>
          {invalidFields.map(({ field, reason }) => (
            <div
              key={field.id}
              className="tw-flex tw-items-center tw-justify-between tw-gap-3"
            >
              <span className="tw-text-sm tw-text-amber-800">
                <span className="tw-font-medium">
                  {field.name || field.field_id}
                </span>{" "}
                — {reason}
              </span>
              <Button
                type="button"
                variant="secondary-alt"
                size="sm"
                compact
                onClick={() => setEditingFieldId(field.id)}
              >
                Edit field
              </Button>
            </div>
          ))}
        </div>
      )}

      {editingFieldId && (
        <CustomerFieldEditModal
          customerFieldId={editingFieldId}
          onClose={() => setEditingFieldId(null)}
          onSave={() => setEditingFieldId(null)}
        />
      )}
    </div>
  );
};
